import {
  Browsers,
  ClockUser,
  DesktopTower,
  MapPin,
  SignOut,
} from "@phosphor-icons/react";
import {
  ArcElement,
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Legend,
  type TooltipItem,
} from "chart.js";
import dayjs from "dayjs";
import { Badge, Button, Modal } from "flowbite-react";
import { useEffect, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  BrowserChrome,
  BrowserEdge,
  BrowserFireFox,
  BrowserSafari,
  DeviceDesktop,
  DevicePhone,
  DeviceTablet,
  IconMapPin,
  OSLinux,
  OSMac,
  OSUnknown,
  OSWindows,
} from "../../../components/Icons";
import SessionsSkeleton from '../../../components/SkeletonLoaders/SessionsSkeleton';
import { useAuth } from "../../../hooks";
import { httpService } from "../../../services/httpService";
import { TabType } from "../container";
import { ISessionItem } from "../interface";

ChartJS.register(ArcElement, ChartTooltip, Legend);

interface Props {
  sessions: ISessionItem[];
  isLoading: boolean;
  activeTab: TabType;
}

const Sessions: React.FC<Props> = ({ sessions, isLoading, activeTab }) => {
  const { t } = useTranslation();
  const { wsConnected, lastPing, sendLogout } = useAuth();
  const [selected, setSelected] = useState<ISessionItem | null>(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [sessionToLogout, setSessionToLogout] = useState<ISessionItem | null>(
    null
  );
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Filter sessions by active/past
  const filteredSessions = useMemo(() => {
    if (!sessions) return [];
    if (activeTab === "active") {
      return sessions.filter((s) => s.is_active);
    }
    return sessions.filter((s) => !s.is_active);
  }, [sessions, activeTab]);

  // Auto-select first session when filtered sessions change
  useEffect(() => {
    if (filteredSessions && filteredSessions.length > 0) {
      // If no session is selected, select the first one
      if (!selected) {
        setSelected(filteredSessions[0]);
      }
      // If the currently selected session is not in the filtered list,
      // select the first session from the filtered list
      else if (!filteredSessions.find((s) => s.id === selected.id)) {
        setSelected(filteredSessions[0]);
      }
    } else {
      setSelected(null);
    }
  }, [filteredSessions, selected]);

  // Handle logout session
  const handleLogoutSession = async (session: ISessionItem) => {
    setIsLoggingOut(true);
    try {
      // Send logout action via WebSocket
      const payload = {
        type: "logout",
        user_id: session.user.toString(),
        session_id: session.session_token,
        time: new Date().toISOString(),
      };

      // Send logout message via WebSocket
      const success = sendLogout(payload);

      if (success) {
        toast.success(`Session logout request sent successfully`);
      } else {
        toast.error("Failed to send logout request - WebSocket not connected");
      }

      setShowLogoutModal(false);
      setSessionToLogout(null);
    } catch (error) {
      toast.error("Failed to logout session");
      console.error("Logout session error:", error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Compute chart data for selected session (define before any early returns to satisfy hooks rule)
  const chart = useMemo(() => {
    const s = selected;
    if (!s) return null;
    // Base minutes from API
    let upMin = Math.max(0, s.uptime_minutes || 0);
    let downMin = Math.max(0, s.downtime_minutes || 0);
    // If WS connected and this is the current session, prefer live ping payload
    const isCurrent = s.session_token === httpService.getSessionId();
    if (wsConnected && isCurrent && lastPing) {
      if (typeof lastPing.uptime === "number")
        upMin = Math.max(0, lastPing.uptime);
      if (typeof lastPing.downtime === "number")
        downMin = Math.max(0, lastPing.downtime);
    }
    const hasAny = upMin + downMin > 0;
    const labelActive = t("configurations.team.sessions.active");
    const labelInactive = t("configurations.team.sessions.inactive");
    const data = hasAny
      ? {
          labels: [labelActive, labelInactive],
          datasets: [
            {
              data: [upMin, downMin],
              backgroundColor: ["#10b981", "#e5e7eb"],
              borderColor: ["#10b981", "#e5e7eb"],
              borderWidth: 1,
            },
          ],
        }
      : {
          labels: [t("common.noData")],
          datasets: [
            {
              data: [1],
              backgroundColor: ["#e5e7eb"],
              borderColor: ["#e5e7eb"],
              borderWidth: 1,
            },
          ],
        };
    const formatHM = (mins: number) => {
      const h = Math.floor(mins / 60);
      const m = Math.round(mins % 60);
      return `${h}h ${m}m`;
    };
    const options = {
      responsive: true,
      cutout: "75%",
      plugins: {
        legend: { position: "bottom" as const },
        tooltip: {
          enabled: hasAny,
          callbacks: {
            label: (ctx: TooltipItem<"doughnut">) =>
              `${ctx.label}: ${formatHM(Number(ctx.raw))}`,
          },
        },
      },
    } as const;
    const start = dayjs(s.start_time).format("MMM D, YYYY h:mm A");
    const end = s.end_time
      ? dayjs(s.end_time).format("MMM D, YYYY h:mm A")
      : "";
    return {
      data,
      options,
      upMin,
      downMin,
      formatHM,
      start,
      end,
      isActive: s.is_active,
    };
  }, [selected, t, wsConnected, lastPing]);

  if (!isLoading && (!sessions || sessions.length === 0)) {
    return (
      <div className="flex-1 flex h-full items-center justify-center text-sm text-gray-500">
        {t("configurations.team.sessions.noSessions")}
      </div>
    );
  }

  const getDeviceIcon = (session: ISessionItem) => {
    if (session.meta?.is_pc) return DeviceDesktop;
    if (session.meta?.is_tablet) return DeviceTablet;
    return DevicePhone;
  };

  const getDeviceType = (session: ISessionItem) => {
    if (session.meta?.is_pc) return "Desktop";
    if (session.meta?.is_tablet) return "Tablet";
    if (session.meta?.is_mobile) return "Mobile";
    return "Desktop";
  };

  const getBrowserIcon = (session: ISessionItem) => {
    const browserFamily = session.meta?.browser?.family?.toLowerCase();
    if (browserFamily?.includes("chrome")) return BrowserChrome;
    if (browserFamily?.includes("firefox")) return BrowserFireFox;
    if (browserFamily?.includes("safari")) return BrowserSafari;
    if (browserFamily?.includes("edge")) return BrowserEdge;
    return Browsers; // fallback to generic browser icon
  };

  const getOsIcon = (session: ISessionItem) => {
    if (session.meta?.os?.family?.includes("Windows")) return OSWindows;
    if (
      session.meta?.os?.family?.includes("macOS") ||
      session.meta?.os?.family?.includes("Mac")
    )
      return OSMac;
    if (
      session.meta?.os?.family?.includes("Linux") ||
      session.meta?.os?.family?.includes("Ubuntu")
    )
      return OSLinux;
    return OSUnknown;
  };

  if (isLoading) return <SessionsSkeleton />;

  return (
    <div className="flex flex-col h-full">
      {filteredSessions.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
          {activeTab === "active"
            ? "No active sessions found"
            : "No past sessions found"}
        </div>
      ) : (
        <div className="flex gap-2 flex-1">
          {/* Left: Sessions List */}
          <div className="w-1/2 ">
            <div className="space-y-2 max-h-[calc(100vh-147px)] overflow-y-auto pr-2">
              {filteredSessions.map((session) => {
                const isCurrentSession =
                  session.session_token === httpService.getSessionId();
                const DeviceIcon = getDeviceIcon(session);
                const OsIcon = getOsIcon(session);
                const BrowserIcon = getBrowserIcon(session);
                const deviceType = getDeviceType(session);
                const browserLabel =
                  [
                    session.meta?.browser?.family,
                    session.meta?.browser?.version,
                  ]
                    .filter(Boolean)
                    .join(" ") || "Unknown Browser";
                const osLabel =
                  [session.meta?.os?.family, session.meta?.os?.version]
                    .filter(Boolean)
                    .join(" ") || "Unknown OS";

                return (
                  <div
                    key={session.id}
                    onClick={() => setSelected(session)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                      selected?.id === session.id
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex gap-3">
                        <ClockUser
                          size={20}
                          className="text-gray-600"
                          weight="duotone"
                        />
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {dayjs(session.start_time).format("MMM D, h:mm A")}
                          </div>
                          <div className="text-xs text-gray-500">
                            {session.end_time
                              ? `Ended ${dayjs(session.end_time).format(
                                  "MMM D, h:mm A"
                                )}`
                              : "Currently active"}
                          </div>
                        </div>
                        {session.is_active && (
                          <Badge color="success" className="text-xs">
                            Active
                          </Badge>
                        )}
                        {isCurrentSession && (
                          <Badge color="warning" className="text-xs">
                            Current
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {session.is_active && !isCurrentSession && (
                          <Button
                            size="xs"
                            color="failure"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSessionToLogout(session);
                              setShowLogoutModal(true);
                            }}
                            className="ml-2"
                          >
                            <div className="flex items-center gap-2">
                              <SignOut size={14} />
                              Revoke
                            </div>
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Device & Location Info */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-gray-600">
                          <DeviceIcon size={18} />
                          <span className="font-medium">{deviceType}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <OsIcon size={18} />
                          <span
                            className={`font-medium ${
                              osLabel.toLowerCase().includes("mac")
                                ? "mt-0.5"
                                : ""
                            }`}
                          >
                            {osLabel}
                          </span>
                        </div>
                        {/* <div className="text-gray-500 ml-3">{osLabel}</div> */}
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-gray-600">
                          <BrowserIcon size={18} />
                          <span className="font-medium">{browserLabel}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <IconMapPin size={18} />
                          <span className="font-medium ">
                            {session.location?.city &&
                            session.location?.country_name
                              ? `${session.location.city}, ${session.location.region},${session.location.country_name}-${session.location.postal}.`
                              : session.ip_address || "Unknown Location"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Activity Time */}
                    <div className="pt-4">
                      <div className="flex items-center gap-4 text-xs">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-green-500 rounded"></div>
                          <span className="text-gray-600">
                            Active:
                            {Math.floor(
                              (session.uptime_minutes || 0) / 60
                            )}h {Math.round((session.uptime_minutes || 0) % 60)}
                            m
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-gray-300 rounded"></div>
                          <span className="text-gray-600">
                            Inactive:{" "}
                            {Math.floor((session.downtime_minutes || 0) / 60)}h{" "}
                            {Math.round((session.downtime_minutes || 0) % 60)}m
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Session Details & Chart */}
          <div className="w-1/2 flex flex-col">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                Select a session to view details
              </div>
            ) : (
              <div className="space-y-2 max-h-[calc(100vh-147px)] overflow-y-auto">
                {/* Session Chart */}
                {chart && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Session Activity
                    </h3>
                    <div className="flex flex-col items-center">
                      <div className="relative min-h-[250px] min-w-[250px] h-[calc(100vh-711px)] w-[calc(100vh-711px)]">
                        <Doughnut data={chart.data} options={chart.options} />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center">
                            <div className="text-xs text-gray-500 mb-1">
                              Total Time
                            </div>
                            <div className="text-lg font-semibold text-gray-900">
                              {chart.formatHM(chart.upMin + chart.downMin)}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-6 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-500 rounded"></div>
                          <span>Active: {chart.formatHM(chart.upMin)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-gray-300 rounded"></div>
                          <span>Inactive: {chart.formatHM(chart.downMin)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Session Details */}
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Session Details
                  </h3>
                  <div className="space-y-4">
                    {/* Time Info */}
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <ClockUser
                          size={16}
                          className="text-gray-600"
                          weight="duotone"
                        />
                        Time Information
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Started:</span>
                          <div className="font-medium">
                            {dayjs(selected.start_time).format(
                              "MMM D, YYYY h:mm A"
                            )}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Ended:</span>
                          <div className="font-medium">
                            {selected.end_time
                              ? dayjs(selected.end_time).format(
                                  "MMM D, YYYY h:mm A"
                                )
                              : "Still active"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <hr />

                    {/* Device Info */}
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                        <DesktopTower
                          size={16}
                          className="text-gray-600"
                          weight="duotone"
                        />
                        Device Information
                      </h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Device Type:</span>
                          <div className="flex items-center gap-2 font-medium">
                            {(() => {
                              const DeviceIcon = getDeviceIcon(selected);
                              return (
                                <DeviceIcon
                                  size={14}
                                  className="text-gray-500"
                                />
                              );
                            })()}
                            {getDeviceType(selected)}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">
                            Operating System:
                          </span>
                          <div className="flex items-center gap-2 font-medium">
                            {(() => {
                              const OsIcon = getOsIcon(selected);
                              return (
                                <OsIcon size={14} className="text-gray-500" />
                              );
                            })()}
                            {[
                              selected.meta?.os?.family,
                              selected.meta?.os?.version,
                            ]
                              .filter(Boolean)
                              .join(" ") || "Unknown"}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Browser:</span>
                          <div className="flex items-center gap-2 font-medium">
                            {(() => {
                              const BrowserIcon = getBrowserIcon(selected);
                              return (
                                <BrowserIcon
                                  size={14}
                                  className="text-gray-500"
                                />
                              );
                            })()}
                            {[
                              selected.meta?.browser?.family,
                              selected.meta?.browser?.version,
                            ]
                              .filter(Boolean)
                              .join(" ") || "Unknown"}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">IP Address:</span>
                          <div className="font-medium font-mono">
                            {selected.ip_address || "Unknown"}
                          </div>
                        </div>
                      </div>
                    </div>

                    <hr />

                    {/* Location Info */}
                    {selected.location && (
                      <div>
                        <h4 className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                          <MapPin
                            size={16}
                            className="text-gray-600"
                            weight="duotone"
                          />
                          Location Information
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">City:</span>
                            <div className="font-medium">
                              {selected.location.city || "Unknown"}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Region:</span>
                            <div className="font-medium">
                              {selected.location.region || "Unknown"}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Country:</span>
                            <div className="font-medium">
                              {selected.location.country_name || "Unknown"}
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">Timezone:</span>
                            <div className="font-medium">
                              {selected.location.timezone || "Unknown"}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      <Modal
        show={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        size="md"
      >
        <Modal.Header>Confirm Session Logout</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to logout this session? This action cannot
              be undone.
            </p>
            {sessionToLogout && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm">
                  <div className="font-medium">Session Details:</div>
                  <div className="text-gray-600 mt-1">
                    Started:{" "}
                    {dayjs(sessionToLogout.start_time).format(
                      "MMM D, YYYY h:mm A"
                    )}
                  </div>
                  <div className="text-gray-600">
                    Device: {getDeviceType(sessionToLogout)}
                  </div>
                  <div className="text-gray-600">
                    IP: {sessionToLogout.ip_address || "Unknown"}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            color="failure"
            onClick={() =>
              sessionToLogout && handleLogoutSession(sessionToLogout)
            }
            disabled={isLoggingOut}
          >
            {isLoggingOut ? "Logging out..." : "Revoke Session"}
          </Button>
          <Button color="gray" onClick={() => setShowLogoutModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default Sessions;
